import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SPA_BUDGET_MAD, hubCollections } from "@majourneys/shared";
import { useDisplayPrice } from "../../../lib/currency";

/** The hubs with a chip row of their own: restaurants and spas. */
export function hasCollections(category: string | null | undefined): boolean {
  return hubCollections(category).length > 0;
}

/** True when `tag` is one of this hub's chips; anything else in a URL is dropped. */
export function isHubCollection(category: string | null | undefined, tag: string | undefined): tag is string {
  return !!tag && hubCollections(category).some((c) => c.id === tag);
}

function keyPrefix(category: string | null | undefined): string {
  return category === "spas" ? "spa_collections" : "restaurant_collections";
}

/**
 * The words on a hub's chips, and on the row's group label. "Under 300 MAD"
 * prints the ceiling in the guest's currency ("Under 28 EUR"), so the label
 * always matches the prices on the cards; the API still judges it in dirhams.
 */
export function useCollectionLabels(category: string | null | undefined) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const prefix = keyPrefix(category);
  const label = useCallback(
    (id: string) =>
      id === "under-300"
        ? t(`${prefix}.${id}`, { price: displayPrice(SPA_BUDGET_MAD) })
        : t(`${prefix}.${id}`),
    [t, prefix, displayPrice],
  );
  return { label, groupLabel: t(`${prefix}.label`) };
}

/** The words on the restaurants hub's cuisine chips, and on the row's group label. */
export function useCuisineLabels() {
  const { t } = useTranslation();
  const label = useCallback((id: string) => t(`restaurant_cuisines.${id}`), [t]);
  return { label, groupLabel: t("restaurant_cuisines.label") };
}
